import { EmptyState } from "@ai-teach/ui-web";
import { GRAMMAR_AZURE_VOICES, GRAMMAR_OPENAI_VOICES, GRAMMAR_TTS_SPEEDS } from "@ai-teach/utils";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MainSidebar } from "../../../components/sidebars/MainSidebar";

/**
 * Speech — grammar-conversation transcript review (admin-only). Surfaces each conversation's
 * transcript plus the EXACT per-turn user message sent to the LLM, so the prompt can be analyzed
 * and tweaked. Backed by the grammar-transcripts domain in b2b-api.
 */

const GRAMMAR_TRANSCRIPTS = gql`
  query GrammarTranscripts($sinceDays: Int, $limit: Int) {
    grammarTranscripts(sinceDays: $sinceDays, limit: $limit) {
      id
      sessionId
      studentId
      slideId
      lessonId
      turnCount
      createdAt
    }
    grammarConversationPrompt
    grammarConversationTeacherName
    grammarConversationTtsProvider
    grammarConversationOpenAIVoice
    grammarConversationAzureVoice
    grammarConversationTtsSpeed
  }
`;

const SET_GRAMMAR_CONVERSATION_TEACHER_NAME = gql`
  mutation SetGrammarConversationTeacherName($name: String!) {
    setGrammarConversationTeacherName(name: $name)
  }
`;

const SET_GRAMMAR_CONVERSATION_TTS_PROVIDER = gql`
  mutation SetGrammarConversationTtsProvider($provider: String!) {
    setGrammarConversationTtsProvider(provider: $provider)
  }
`;

const SET_GRAMMAR_CONVERSATION_OPENAI_VOICE = gql`
  mutation SetGrammarConversationOpenAIVoice($voice: String!) {
    setGrammarConversationOpenAIVoice(voice: $voice)
  }
`;

const SET_GRAMMAR_CONVERSATION_AZURE_VOICE = gql`
  mutation SetGrammarConversationAzureVoice($voice: String!) {
    setGrammarConversationAzureVoice(voice: $voice)
  }
`;

const SET_GRAMMAR_CONVERSATION_TTS_SPEED = gql`
  mutation SetGrammarConversationTtsSpeed($speed: Float!) {
    setGrammarConversationTtsSpeed(speed: $speed)
  }
`;

const GRAMMAR_TRANSCRIPT = gql`
  query GrammarTranscript($id: ID!) {
    grammarTranscript(id: $id) {
      id
      sessionId
      studentId
      slideId
      lessonId
      language
      mode
      systemPromptVersion
      createdAt
      turns {
        turnId
        recognizedText
        assistantText
        noSpeechDetected
        turnState
        timestamp
        isOpening
        openingVisibleSlideId
        visibleSlideId
        visibleWord
        visibleColor
        bubbleImages {
          partIndex
          slideId
          word
        }
        guardHits {
          rule
          partIndex
        }
        ttfMs
        turnTotalServerMs
        llmFirstTokenMs
        llmFirstAttemptMs
        ttsFirstPartMs
        teacherStateComputeMs
        llmUserMessages {
          attempt
          content
          sanitized
        }
      }
    }
  }
`;

interface TranscriptSummary {
  id: string;
  sessionId: string;
  studentId: string | null;
  slideId: string | null;
  lessonId: string | null;
  turnCount: number;
  createdAt: string;
}

interface LlmMessage {
  attempt: number;
  content: string;
  sanitized: boolean;
}

interface BubbleImage {
  partIndex: number;
  slideId: string | null;
  word: string | null;
}

interface GuardHit {
  rule: string;
  partIndex: number;
}

interface TranscriptTurn {
  turnId: string;
  recognizedText: string;
  assistantText: string;
  noSpeechDetected: boolean;
  turnState: string | null;
  timestamp: string;
  isOpening: boolean | null;
  openingVisibleSlideId: string | null;
  visibleSlideId: string | null;
  visibleWord: string | null;
  visibleColor: string | null;
  bubbleImages: BubbleImage[] | null;
  guardHits: GuardHit[] | null;
  ttfMs: number | null;
  turnTotalServerMs: number | null;
  llmFirstTokenMs: number | null;
  llmFirstAttemptMs: number | null;
  ttsFirstPartMs: number | null;
  teacherStateComputeMs: number | null;
  llmUserMessages: LlmMessage[];
}

interface TranscriptDetail {
  id: string;
  sessionId: string;
  studentId: string | null;
  slideId: string | null;
  lessonId: string | null;
  language: string | null;
  mode: string;
  systemPromptVersion: string | null;
  createdAt: string;
  turns: TranscriptTurn[];
}

interface ListData {
  grammarTranscripts: TranscriptSummary[];
  grammarConversationPrompt: string;
  grammarConversationTeacherName: string;
  grammarConversationTtsProvider: string;
  grammarConversationOpenAIVoice: string;
  grammarConversationAzureVoice: string;
  grammarConversationTtsSpeed: number;
}

interface SetTeacherNameData {
  setGrammarConversationTeacherName: string;
}

interface SetTtsProviderData {
  setGrammarConversationTtsProvider: string;
}

interface SetOpenAIVoiceData {
  setGrammarConversationOpenAIVoice: string;
}

interface SetAzureVoiceData {
  setGrammarConversationAzureVoice: string;
}

interface SetTtsSpeedData {
  setGrammarConversationTtsSpeed: number;
}

interface DetailData {
  grammarTranscript: TranscriptDetail | null;
}

const SINCE_OPTIONS = [7, 14, 30] as const;

function fmtDate(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

function fmtMs(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`;
}

function SystemPrompt({ prompt }: { prompt: string }) {
  return (
    <details className="rounded-lg border border-gray-200 dark:border-gray-800">
      <summary className="cursor-pointer px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
        Current system prompt (static)
      </summary>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
        {prompt}
      </pre>
    </details>
  );
}

/** A labelled dropdown that saves a single grammar-conversation setting (provider or voice) on Save. */
function SelectSetting({
  label,
  hint,
  value: current,
  options,
  disabled,
  onSave,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string; detail?: string }>;
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
  /** Fires on every dropdown change (before Save) — e.g. to let a sibling field react to the pick. */
  onChange?: (value: string) => void;
}) {
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Re-seed if the fetched value changes (e.g. after a refetch).
  useEffect(() => setValue(current), [current]);

  const save = async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      await onSave(value);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-semibold text-gray-700 dark:text-gray-300">{label}</span>
          <span className="text-xs text-gray-500">{hint}</span>
          <select
            value={value}
            disabled={disabled}
            onChange={(e) => {
              setValue(e.target.value);
              onChange?.(e.target.value);
            }}
            className="mt-1 w-64 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.detail ? `${o.label} — ${o.detail}` : o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={save}
          disabled={disabled || saving || value === current}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {errorMsg && <p className="mt-2 text-xs text-red-500">Couldn't save: {errorMsg}</p>}
    </div>
  );
}

const TTS_PROVIDER_OPTIONS = [
  { value: "azure", label: "Azure Speech", detail: "East Asia · low latency (default)" },
  { value: "openai", label: "OpenAI", detail: "US · richer voices, trans-Pacific hop" },
] as const;

/** The teacher name + provider + both voice pickers. The name auto-suggests from the Azure voice
 *  (unless customized), so the voice a student hears matches the name the teacher introduces herself by. */
function ConversationTtsSettings({
  teacherName,
  provider,
  openAIVoice,
  azureVoice,
  ttsSpeed,
  onSaved,
}: {
  teacherName: string;
  provider: string;
  openAIVoice: string;
  azureVoice: string;
  ttsSpeed: number;
  onSaved: () => void;
}) {
  const [setProvider] = useMutation<SetTtsProviderData>(SET_GRAMMAR_CONVERSATION_TTS_PROVIDER);
  const [setOpenAIVoice] = useMutation<SetOpenAIVoiceData>(SET_GRAMMAR_CONVERSATION_OPENAI_VOICE);
  const [setAzureVoice] = useMutation<SetAzureVoiceData>(SET_GRAMMAR_CONVERSATION_AZURE_VOICE);
  const [setTtsSpeed] = useMutation<SetTtsSpeedData>(SET_GRAMMAR_CONVERSATION_TTS_SPEED);
  const [saveName, { loading: savingName, error: nameError }] = useMutation<SetTeacherNameData>(
    SET_GRAMMAR_CONVERSATION_TEACHER_NAME
  );
  const isAzure = provider === "azure";

  // Teacher name. Re-seed ONLY from the fetched name (not the voice) — a voice Save refetches but
  // doesn't change grammarConversationTeacherName, so a pending name suggestion is preserved.
  const [name, setName] = useState(teacherName);
  useEffect(() => setName(teacherName), [teacherName]);

  const saveTeacherName = async () => {
    try {
      await saveName({ variables: { name: name.trim() } });
      onSaved();
    } catch {
      // Apollo populates `nameError` (rendered below); the catch only prevents an unhandled rejection.
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-gray-700 dark:text-gray-300">Teacher name</span>
            <span className="text-xs text-gray-500">
              The name the AI teacher introduces herself by. Auto-suggested from the Azure voice —
              edit to override.
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jenny"
              className="mt-1 w-48 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={saveTeacherName}
            disabled={savingName || name.trim() === teacherName}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {savingName ? "Saving…" : "Save"}
          </button>
        </div>
        {nameError && (
          <p className="mt-2 text-xs text-red-500">Couldn't save: {nameError.message}</p>
        )}
      </div>

      <SelectSetting
        label="Conversation TTS provider"
        hint="Which engine speaks the AI teacher's replies. Azure is in-region (lowest latency); OpenAI has richer voices but is US-served."
        value={provider}
        options={TTS_PROVIDER_OPTIONS}
        onSave={async (v) => {
          await setProvider({ variables: { provider: v } });
          onSaved();
        }}
      />
      <SelectSetting
        label={`Azure voice${isAzure ? " (active)" : ""}`}
        hint="Neural voice used when the provider is Azure Speech."
        value={azureVoice}
        options={GRAMMAR_AZURE_VOICES}
        disabled={!isAzure}
        onChange={(v) => {
          // Auto-suggest the teacher name from the picked voice, unless the admin set a custom name.
          const t = name.trim();
          const looksAuto =
            t === "" || t === "Jenny" || GRAMMAR_AZURE_VOICES.some((x) => x.label === t);
          if (!looksAuto) return;
          const label = GRAMMAR_AZURE_VOICES.find((x) => x.value === v)?.label;
          if (label) setName(label);
        }}
        onSave={async (v) => {
          await setAzureVoice({ variables: { voice: v } });
          onSaved();
        }}
      />
      <SelectSetting
        label={`OpenAI voice${isAzure ? "" : " (active)"}`}
        hint="Voice used when the provider is OpenAI."
        value={openAIVoice}
        options={GRAMMAR_OPENAI_VOICES}
        disabled={isAzure}
        onSave={async (v) => {
          await setOpenAIVoice({ variables: { voice: v } });
          onSaved();
        }}
      />
      <SelectSetting
        label="Speaking rate"
        hint="How fast the AI teacher speaks. Lower is slower — helps beginners follow along."
        value={ttsSpeed.toFixed(2)}
        options={GRAMMAR_TTS_SPEEDS}
        onSave={async (v) => {
          await setTtsSpeed({ variables: { speed: Number(v) } });
          onSaved();
        }}
      />
    </div>
  );
}

function TurnView({
  turn,
  index,
  teacherName,
}: { turn: TranscriptTurn; index: number; teacherName: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
      <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
        <span className="font-semibold">{turn.isOpening ? "Opener" : `Turn ${index}`}</span>
        {turn.isOpening && (
          <span className="rounded bg-primary-100 px-1.5 py-0.5 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
            opening question
          </span>
        )}
        {turn.isOpening && turn.openingVisibleSlideId && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono dark:bg-gray-800">
            img {turn.openingVisibleSlideId}
          </span>
        )}
        {!turn.isOpening && turn.noSpeechDetected && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            no speech
          </span>
        )}
        {turn.turnState && turn.turnState !== "normal" && !turn.isOpening && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-800">
            {turn.turnState}
          </span>
        )}
        {/* v4: the streaming semantic guard fired this turn — the LLM ignored the response plan and a
            bubble was replaced/dropped/injected. Shows how often (and which rule) so we can tune it. */}
        {turn.guardHits?.map((g, i) => (
          <span
            key={`${g.rule}-${g.partIndex}-${i}`}
            className="rounded bg-purple-100 px-1.5 py-0.5 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
            title={`guard: ${g.rule} @ bubble ${g.partIndex}`}
          >
            guard: {g.rule}
          </span>
        ))}
        {!turn.isOpening && (
          <span className="ml-auto flex flex-wrap items-center gap-x-2 tabular-nums">
            <span>felt {fmtMs(turn.ttfMs)}</span>
            <span
              className={
                (turn.llmFirstTokenMs ?? 0) >= 5000
                  ? "rounded bg-red-100 px-1 font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  : undefined
              }
            >
              llm-tok {fmtMs(turn.llmFirstTokenMs)}
            </span>
            <span>llm {fmtMs(turn.llmFirstAttemptMs)}</span>
            <span>ts {fmtMs(turn.teacherStateComputeMs)}</span>
            <span>tts {fmtMs(turn.ttsFirstPartMs)}</span>
            <span>server {fmtMs(turn.turnTotalServerMs)}</span>
          </span>
        )}
      </div>

      {/* Round 9: the image shown this turn + its word/color, and the per-bubble timeline — so a reviewer
          can see WHEN each image appeared (image↔speech sync). */}
      {!turn.isOpening && (turn.visibleWord || turn.visibleSlideId) && (
        <p className="mb-1 text-sm">
          <span className="font-semibold text-emerald-600">Image:</span>{" "}
          <span className="text-gray-800 dark:text-gray-200">
            {turn.visibleWord || "—"}
            {turn.visibleColor ? ` (${turn.visibleColor})` : ""}
          </span>{" "}
          <span className="font-mono text-[10px] text-gray-400">{turn.visibleSlideId ?? ""}</span>
          {turn.bubbleImages && turn.bubbleImages.length > 0 && (
            <span className="ml-2 text-[10px] text-gray-400">
              bubbles:{" "}
              {turn.bubbleImages
                .map((b) => `#${b.partIndex}:${b.word ?? b.slideId ?? "—"}`)
                .join(" → ")}
            </span>
          )}
        </p>
      )}

      {!turn.isOpening && (
        <p className="mb-1 text-sm">
          <span className="font-semibold text-gray-500">Student:</span>{" "}
          <span className="text-gray-800 dark:text-gray-200">{turn.recognizedText || "—"}</span>
        </p>
      )}
      <p className="text-sm">
        <span className="font-semibold text-primary-600">{teacherName}:</span>{" "}
        <span className="text-gray-800 dark:text-gray-200">{turn.assistantText || "—"}</span>
      </p>

      {!turn.isOpening && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            Context sent to the LLM ({turn.llmUserMessages.length})
          </summary>
          <div className="mt-2 flex flex-col gap-2">
            {turn.llmUserMessages.map((m) => (
              <div key={`${turn.turnId}-${m.attempt}`}>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">
                  attempt {m.attempt}
                  {m.sanitized ? " · sanitized (content-filter retry)" : ""}
                </div>
                <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                  {m.content}
                </pre>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function TranscriptDetailView({ id, teacherName }: { id: string; teacherName: string }) {
  const { data, loading, error } = useQuery<DetailData>(GRAMMAR_TRANSCRIPT, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  if (error) return <EmptyState title="Couldn't load transcript" description={error.message} />;
  if (loading && !data) return <p className="text-sm text-gray-500">Loading…</p>;
  const t = data?.grammarTranscript;
  if (!t)
    return <EmptyState title="Transcript not found" description="It may have been removed." />;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm text-gray-500">
        <div>
          slide <span className="font-mono">{t.slideId ?? "—"}</span> · lesson{" "}
          <span className="font-mono">{t.lessonId ?? "—"}</span> · student{" "}
          <span className="font-mono">{t.studentId ?? "—"}</span>
        </div>
        <div>
          {fmtDate(t.createdAt)} · prompt v{t.systemPromptVersion ?? "?"} · {t.turns.length} turns
        </div>
      </div>
      {(() => {
        // Number only conversational turns (1-based); the opener turn carries no number.
        let turnNumber = 0;
        return t.turns.map((turn) => {
          if (!turn.isOpening) turnNumber += 1;
          return (
            <TurnView key={turn.turnId} turn={turn} index={turnNumber} teacherName={teacherName} />
          );
        });
      })()}
    </div>
  );
}

function SpeechReviewPage() {
  const [sinceDays, setSinceDays] = useState<number>(30);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<ListData>(GRAMMAR_TRANSCRIPTS, {
    variables: { sinceDays, limit: 50 },
    fetchPolicy: "cache-and-network",
  });

  const transcripts = data?.grammarTranscripts ?? [];

  return (
    <div className="flex h-screen">
      <MainSidebar />
      <div className="flex-1 overflow-auto p-6">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="h3">Speech — Grammar Conversation Review</h1>
            <p className="text-sm text-gray-500">
              Review transcripts and the exact prompt context each turn received, to analyze and
              tweak the prompt.
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-800 p-1">
            {SINCE_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setSinceDays(d)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  sinceDays === d
                    ? "bg-primary-600 text-white"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {data?.grammarConversationTtsProvider !== undefined && (
          <div className="mb-4">
            <ConversationTtsSettings
              teacherName={data.grammarConversationTeacherName}
              provider={data.grammarConversationTtsProvider}
              openAIVoice={data.grammarConversationOpenAIVoice}
              azureVoice={data.grammarConversationAzureVoice}
              ttsSpeed={data.grammarConversationTtsSpeed}
              onSaved={() => {
                void refetch();
              }}
            />
          </div>
        )}

        {data?.grammarConversationPrompt && (
          <div className="mb-4">
            <SystemPrompt prompt={data.grammarConversationPrompt} />
          </div>
        )}

        {error ? (
          <EmptyState
            title="Couldn't load transcripts"
            description={/admin/i.test(error.message) ? "This view is admin-only." : error.message}
          />
        ) : loading && transcripts.length === 0 ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : transcripts.length === 0 ? (
          <EmptyState
            title="No transcripts yet"
            description="Grammar conversations will appear here once students (or you) run them."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
            <div className="flex flex-col gap-1">
              {transcripts.map((tr) => (
                <button
                  key={tr.id}
                  type="button"
                  onClick={() => setSelectedId(tr.id)}
                  className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    selectedId === tr.id
                      ? "border-primary-400 bg-primary-50/60 dark:bg-primary-900/20"
                      : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }`}
                >
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    {tr.turnCount} turns
                  </div>
                  <div className="text-xs text-gray-500">{fmtDate(tr.createdAt)}</div>
                  <div className="text-[11px] text-gray-400 font-mono truncate">
                    slide {tr.slideId ?? "—"}
                  </div>
                </button>
              ))}
            </div>
            <div>
              {selectedId ? (
                <TranscriptDetailView
                  id={selectedId}
                  teacherName={data?.grammarConversationTeacherName || "Teacher"}
                />
              ) : (
                <p className="text-sm text-gray-500">Select a transcript to review.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/admin/speech/")({
  component: SpeechReviewPage,
});
